"use client";

import {
  ArrowRight,
  CheckCircle,
  Clock,
  Database,
  Filter,
  Mail,
  MessageSquare,
  Webhook,
  Zap,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FadeInUp } from "@/components/ui/motion";
import { useIsMobile } from "@/hooks/use-mobile";
import { trackLeadSignup } from "@/lib/analytics";

interface Template {
  id: string;
  category: "ops" | "founders" | "developers";
  name: string;
  outcome: string;
  nodes: { icon: React.ElementType; label: string }[];
  description: string;
  whenToUse: string;
}

const templates: Template[] = [
  {
    id: "lead-capture",
    category: "ops",
    name: "Lead Capture Pipeline",
    outcome: "Route leads to CRM and notify sales instantly",
    nodes: [
      { icon: Webhook, label: "Form" },
      { icon: Database, label: "Enrich" },
      { icon: CheckCircle, label: "Verify" },
      { icon: Mail, label: "Notify" },
    ],
    description:
      "Automatically captures form submissions, enriches contact data with company info, verifies email validity, and pings your sales team.",
    whenToUse: "When you need leads to reach sales within seconds, not hours.",
  },
  {
    id: "billing-alerts",
    category: "founders",
    name: "Billing Event Alerts",
    outcome: "Never miss a payment event-success or failure",
    nodes: [
      { icon: Webhook, label: "Stripe" },
      { icon: Filter, label: "Filter" },
      { icon: Database, label: "Log" },
      { icon: MessageSquare, label: "Slack" },
    ],
    description:
      "Listens to Stripe webhooks for payments, failures, and subscription changes, logs them to your DB, and routes alerts to Slack.",
    whenToUse: "When you need real-time visibility into revenue events.",
  },
  {
    id: "webhook-router",
    category: "developers",
    name: "Webhook Router",
    outcome: "Fan out webhooks to multiple destinations",
    nodes: [
      { icon: Webhook, label: "Receive" },
      { icon: Filter, label: "Route" },
      { icon: Zap, label: "Transform" },
      { icon: Database, label: "Deliver" },
    ],
    description:
      "Receives webhooks from any source, applies conditional logic, transforms the payload, and routes to different endpoints.",
    whenToUse:
      "When you need to fan out webhooks without writing custom routing code.",
  },
  {
    id: "scheduled-reports",
    category: "ops",
    name: "Scheduled Reports",
    outcome: "Automated reports delivered on schedule",
    nodes: [
      { icon: Clock, label: "Schedule" },
      { icon: Database, label: "Query" },
      { icon: Filter, label: "Analyze" },
      { icon: Mail, label: "Send" },
    ],
    description:
      "Runs on a schedule to pull metrics from your data sources, analyzes the trends, and delivers a summary to your inbox.",
    whenToUse: "When you want daily/weekly summaries without manual work.",
  },
  {
    id: "user-onboarding",
    category: "founders",
    name: "Onboarding Sequence",
    outcome: "Guide new users automatically",
    nodes: [
      { icon: Webhook, label: "Signup" },
      { icon: Filter, label: "Check" },
      { icon: Database, label: "Update" },
      { icon: Mail, label: "Email" },
    ],
    description:
      "Monitors new user progress, updates their onboarding status in your database, and sends contextual tips based on usage.",
    whenToUse: "When you want every new user to get a personalized experience.",
  },
  {
    id: "error-alerting",
    category: "developers",
    name: "Error Alerting",
    outcome: "Get instant alerts when things break",
    nodes: [
      { icon: Webhook, label: "Error" },
      { icon: Filter, label: "Dedupe" },
      { icon: Database, label: "Store" },
      { icon: Zap, label: "Alert" },
    ],
    description:
      "Aggregates errors from monitoring tools, deduplicates noise, stores the incident, and sends actionable alerts to your team.",
    whenToUse:
      "When you want to catch production issues before users report them.",
  },
];

const categories = [
  { id: "all", label: "All" },
  { id: "ops", label: "Ops & Marketing" },
  { id: "founders", label: "Founders" },
  { id: "developers", label: "Developers" },
];

const getCategoryStyles = (category: string) => {
  switch (category) {
    case "ops":
      return "bg-primary/10 text-primary border-primary/20";
    case "founders":
      return "bg-success/10 text-success border-success/20";
    case "developers":
      return "bg-warning/10 text-warning border-warning/20";
    default:
      return "bg-muted text-muted-foreground border-border";
  }
};

export function Templates() {
  const [activeCategory, setActiveCategory] = useState<string>("all");
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(
    null,
  );
  const [hoveredTemplate, setHoveredTemplate] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const navigate = useRouter();

  const filteredTemplates =
    activeCategory === "all"
      ? templates
      : templates.filter((t) => t.category === activeCategory);

  const handleUseTemplate = (templateId: string, category?: string) => {
    setSelectedTemplate(null);
    trackLeadSignup("landing_templates", {
      template: templateId,
      category: category || "unknown",
    });
    navigate.push(`/register?template=${templateId}`);
  };

  return (
    <section id="templates" className="py-20 lg:py-28 section-elevated">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <FadeInUp className="max-w-2xl mx-auto text-center mb-12">
          <h2 className="text-foreground mb-4">
            Start with a <span className="gradient-text">template</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Pre-built workflows for common use cases. Customize and deploy in
            minutes.
          </p>
        </FadeInUp>

        {/* Category Tabs */}
        <FadeInUp
          delay={0.1}
          className="flex flex-wrap justify-center gap-2 mb-10"
        >
          {categories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeCategory === cat.id
                  ? "bg-primary text-primary-foreground"
                  : "bg-card border border-border text-muted-foreground hover:text-foreground hover:border-muted-foreground/30"
              }`}
            >
              {cat.label}
            </button>
          ))}
        </FadeInUp>

        {/* Template Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
          <AnimatePresence mode="popLayout">
            {filteredTemplates.map((template) => (
              <motion.div
                key={template.id}
                layout
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.96 }}
                whileHover={isMobile ? {} : { y: -2 }}
                transition={{ duration: 0.2 }}
                onClick={() => setSelectedTemplate(template)}
                onMouseEnter={() =>
                  !isMobile && setHoveredTemplate(template.id)
                }
                onMouseLeave={() => !isMobile && setHoveredTemplate(null)}
                className="group relative p-4 sm:p-5 rounded-xl card-interactive cursor-pointer flex flex-col h-full"
              >
                {/* Category badge */}
                <div className="flex items-center justify-between mb-4">
                  <span
                    className={`px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-md text-[9px] sm:text-[10px] font-semibold uppercase tracking-wider border ${getCategoryStyles(template.category)}`}
                  >
                    {template.category}
                  </span>
                  <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-300" />
                </div>

                {/* Title + outcome */}
                <h3 className="text-sm sm:text-base font-semibold text-foreground mb-2">
                  {template.name}
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground mb-6 line-clamp-2">
                  {template.outcome}
                </p>

                {/* Mini workflow preview */}
                <div className="mt-auto relative flex items-center justify-between gap-1 sm:gap-2 py-2 sm:py-3 px-2 sm:px-3 rounded-lg bg-muted/50 overflow-hidden group/preview">
                  {/* Moving glow effect */}
                  <AnimatePresence>
                    {hoveredTemplate === template.id && (
                      <motion.div
                        initial={{ x: "-100%", opacity: 0 }}
                        animate={{ x: "250%", opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          repeatDelay: 0.5,
                          ease: "easeInOut",
                        }}
                        className="absolute inset-0 w-1/2 bg-gradient-to-r from-transparent via-primary/10 to-transparent -skew-x-12 pointer-events-none"
                      />
                    )}
                  </AnimatePresence>

                  {template.nodes.map((node, idx) => (
                    <div
                      key={`${template.id}-${node.label}`}
                      className="flex items-center gap-1 sm:gap-2 relative z-10 flex-1 justify-center last:flex-none"
                    >
                      <motion.div
                        animate={
                          hoveredTemplate === template.id
                            ? {
                                scale: [1, 1.1, 1],
                              }
                            : {
                                scale: 1,
                              }
                        }
                        transition={{
                          delay:
                            hoveredTemplate === template.id ? idx * 0.25 : 0,
                          duration: 0.4,
                        }}
                        className={`w-6 h-6 sm:w-8 sm:h-8 rounded-md flex items-center justify-center border transition-all duration-500 ${
                          hoveredTemplate === template.id
                            ? "bg-primary/15 border-primary/40 text-primary shadow-[0_0_15px_rgba(59,130,246,0.1)]"
                            : "bg-card border-border text-muted-foreground"
                        }`}
                        style={{
                          transitionDelay:
                            hoveredTemplate === template.id
                              ? `${idx * 0.25}s`
                              : "0s",
                        }}
                      >
                        <node.icon className="w-3 h-3 sm:w-4 sm:h-4" />
                      </motion.div>
                      {idx < template.nodes.length - 1 && (
                        <div className="flex-1 h-0.5 bg-border/50 rounded-full overflow-hidden relative min-w-[8px] sm:min-w-[12px]">
                          <motion.div
                            animate={
                              hoveredTemplate === template.id
                                ? {
                                    left: ["-100%", "200%"],
                                  }
                                : {
                                    left: "-100%",
                                  }
                            }
                            transition={{
                              delay: idx * 0.25 + 0.12,
                              duration: 1,
                              repeat: Infinity,
                              repeatDelay: 0.5,
                              ease: "easeInOut",
                            }}
                            className="absolute inset-0 bg-gradient-to-r from-transparent via-primary to-transparent"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Browse All */}
        <FadeInUp delay={0.3} className="text-center mt-10">
          <Button variant="outline" onClick={() => navigate.push("/templates")}>
            Browse all templates
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </FadeInUp>
      </div>

      {/* Template Modal */}
      <Dialog
        open={!!selectedTemplate}
        onOpenChange={() => setSelectedTemplate(null)}
      >
        <DialogContent className="sm:max-w-md w-[95vw] sm:w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedTemplate?.name}</DialogTitle>
          </DialogHeader>

          <div className="space-y-6 pt-2">
            {/* Mini workflow */}
            <div className="flex items-center justify-center gap-2 sm:gap-4 py-6 px-2 sm:px-4 rounded-xl bg-muted/30 border border-border/50 overflow-x-auto">
              <div className="flex items-center gap-2 sm:gap-4 min-w-max mx-auto">
                {selectedTemplate?.nodes.map((node, idx) => (
                  <div
                    key={`${selectedTemplate?.id ?? "template"}-${node.label}`}
                    className="flex items-center gap-2 sm:gap-4"
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.1 + 0.2 }}
                      className="flex flex-col items-center gap-2"
                    >
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-card border border-border shadow-sm flex items-center justify-center text-primary">
                        <node.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                      </div>
                      <span className="text-[9px] sm:text-[10px] text-muted-foreground font-semibold uppercase tracking-tight">
                        {node.label}
                      </span>
                    </motion.div>
                    {idx < (selectedTemplate?.nodes.length ?? 0) - 1 && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: idx * 0.1 + 0.25 }}
                      >
                        <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 text-muted-foreground/30 mb-6" />
                      </motion.div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">
                What it does
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {selectedTemplate?.description}
              </p>
            </div>

            <div>
              <h4 className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2">
                When to use
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {selectedTemplate?.whenToUse}
              </p>
            </div>

            <Button
              className="w-full"
              variant="gradient"
              onClick={() =>
                selectedTemplate &&
                handleUseTemplate(
                  selectedTemplate.id,
                  selectedTemplate.category,
                )
              }
            >
              Use this template
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
